import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty, STAGE_STYLES, fmtDate } from "@/components/shared";
import LeadDrawer from "@/components/LeadDrawer";
import ImportExport from "@/components/ImportExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, LayoutGrid, List, Phone, MapPin, Loader2, Trash2, UserPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { DateRange, FilterSelect, FilterShell, presetRange, toApiRange } from "@/components/Filters";
import { toast } from "sonner";

const PAGE_SIZE = 50;

function Pager({ page, total, setPage }) {
  const pages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, total || 0);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t text-sm" data-testid="lead-pager">
      <span className="text-slate-500" data-testid="lead-pager-info">Showing {from}–{to} of {total || 0} · Page {page} of {pages}</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="lead-page-prev"><ChevronLeft className="w-4 h-4" /> Previous</Button>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="lead-page-next">Next <ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function BulkAssignDialog({ open, setOpen, ids, staff, role, onDone }) {
  const [to, setTo] = useState("");
  const options = (staff || []).filter((s) => s.active !== false && s.role !== "marketing" && (role === "admin" ? true : s.role === "sales"));
  const assign = useMutation({
    mutationFn: async () => (await api.post("/leads/bulk-assign", { ids, assigned_to: to })).data,
    onSuccess: (d) => { toast.success(`Assigned ${d.assigned} lead(s)`); setTo(""); setOpen(false); onDone(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="bulk-assign-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" /> Assign {ids.length} lead(s)</DialogTitle></DialogHeader>
        <Label>Assign to {role === "admin" ? "staff member" : "sales person"}</Label>
        <Select value={to} onValueChange={setTo}>
          <SelectTrigger data-testid="bulk-assign-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{options.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`bulk-assign-opt-${s.id}`}>{s.name} ({s.role})</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="bulk-assign-cancel">Cancel</Button>
          <Button className="bg-brand-blue hover:bg-blue-700" disabled={!to || assign.isPending} onClick={() => assign.mutate()} data-testid="bulk-assign-confirm">
            {assign.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Assign ${ids.length} lead(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const OCCUPATIONS = ["student", "working professional", "business owner", "homemaker", "other"];

function CreateLeadDialog({ config }) {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: courses } = useQuery({ queryKey: ["courses"], queryFn: async () => (await api.get("/settings/courses")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", source: "walk-in", course_interest: "", locality: "", occupation: "", priority: "medium", notes: "" });
  const [dupe, setDupe] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = useMutation({
    mutationFn: async (force) => (await api.post("/leads", { ...form, force })).data,
    onSuccess: (data) => {
      if (data.duplicate) { setDupe(data); toast.warning("Possible duplicate found"); return; }
      toast.success("Lead created");
      qc.invalidateQueries(); setOpen(false); setForm({ name: "", phone: "", source: "walk-in", course_interest: "", locality: "", occupation: "", priority: "medium", notes: "" }); setDupe(null);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setDupe(null); }}>
      <DialogTrigger asChild>
        <Button className="bg-brand-blue hover:bg-blue-700 gap-2" data-testid="add-lead-btn"><Plus className="w-4 h-4" /> Add Lead</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
        {dupe ? (
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 p-3 text-sm">
              A lead with this phone already exists: <b>{dupe.lead.name}</b> (stage {dupe.lead.stage}). We added a repeat enquiry event and preserved history. Shared family numbers are allowed — create a separate lead only if this is a different person.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); setDupe(null); }} data-testid="dupe-keep">OK, use existing</Button>
              <Button className="flex-1 bg-orange-600 hover:bg-orange-700" onClick={() => create.mutate(true)} data-testid="dupe-force">Different person — create new</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input data-testid="lead-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div><Label>Phone *</Label><Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="98765xxxxx" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Source *</Label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{(settings?.lead_sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Course interest</Label>
                <Select value={form.course_interest} onValueChange={(v) => set("course_interest", v)}>
                  <SelectTrigger data-testid="lead-course"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(courses || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Locality</Label><Input data-testid="lead-locality" value={form.locality} onChange={(e) => set("locality", e.target.value)} /></div>
              <div><Label>Occupation</Label>
                <Select value={form.occupation} onValueChange={(v) => set("occupation", v)}>
                  <SelectTrigger data-testid="lead-occupation"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{OCCUPATIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea data-testid="lead-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Hinglish notes OK — e.g. Kal demo ke liye aayenge" /></div>
          </div>
        )}
        {!dupe && (
          <DialogFooter>
            <Button disabled={create.isPending || !form.name || !form.phone} className="bg-brand-blue hover:bg-blue-700" onClick={() => create.mutate(false)} data-testid="lead-save">
              {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Lead"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LeadCard({ lead, onOpen }) {
  return (
    <div onClick={() => onOpen(lead.id)} data-testid={`lead-card-${lead.id}`}
      className="p-3 rounded-lg border bg-white dark:bg-slate-800 hover:shadow-md cursor-pointer transition-shadow space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-sm truncate">{lead.name}</div>
        {lead.priority === "high" && <span className="w-2 h-2 rounded-full bg-orange-500 mt-1.5 shrink-0" />}
      </div>
      <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.phone}</div>
      {lead.locality && <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.locality}</div>}
      {lead.course_interest && <Badge variant="secondary" className="text-[10px]">{lead.course_interest}</Badge>}
    </div>
  );
}

export default function Leads() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("list");
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");
  const [priority, setPriority] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [assignedVia, setAssignedVia] = useState("");
  const [lastOutcome, setLastOutcome] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const { data: creators } = useQuery({ queryKey: ["lead-creators"], queryFn: async () => (await api.get("/leads/meta/creators")).data });
  const [stale, setStale] = useState("");
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState(presetRange("all"));
  const [unassigned, setUnassigned] = useState(params.get("unassigned") === "1");
  const [selected, setSelected] = useState(() => new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [page, setPageRaw] = useState(1);
  const canSelect = ["admin", "reception"].includes(user?.role);
  const setPage = (p) => { setPageRaw(p); setSelected(new Set()); };
  const openId = params.get("id");

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (items) => setSelected((s) => (s.size >= (items?.length || 0) ? new Set() : new Set((items || []).map((l) => l.id))));

  const bulkDelete = useMutation({
    mutationFn: async () => (await api.post("/leads/bulk-delete", { ids: [...selected] })).data,
    onSuccess: (d) => {
      toast.success(`Deleted ${d.deleted} lead(s)${d.skipped?.length ? ` — ${d.skipped.length} skipped (have admissions)` : ""}`);
      if (d.skipped?.length) toast.warning(`Skipped with admission: ${d.skipped.join(", ")}`);
      setSelected(new Set()); setDelOpen(false); qc.invalidateQueries();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const { data: config } = useQuery({ queryKey: ["leadconfig"], queryFn: async () => (await api.get("/leads/meta/config")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/users")).data, enabled: ["admin", "reception"].includes(user?.role) });
  const leadParams = { q: q || undefined, stage: stage || undefined, source: source || undefined, priority: priority || undefined, assigned_to: assignedTo || undefined, assigned_via: assignedVia || undefined, last_outcome: lastOutcome || undefined, created_by: createdBy || undefined, stale_days: stale || undefined, unassigned: unassigned || undefined, ...toApiRange(range) };
  const filterKey = JSON.stringify(leadParams);
  const [lastKey, setLastKey] = useState(filterKey);
  if (lastKey !== filterKey) { setLastKey(filterKey); setPageRaw(1); setSelected(new Set()); }
  const { data: list, isLoading } = useQuery({
    queryKey: ["leads", leadParams, page],
    queryFn: async () => (await api.get("/leads", { params: { ...leadParams, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE } })).data,
    enabled: view === "list",
    placeholderData: (prev) => prev,
  });
  const clearFilters = () => { setStage(""); setSource(""); setPriority(""); setAssignedTo(""); setAssignedVia(""); setLastOutcome(""); setCreatedBy(""); setStale(""); setPreset("all"); setRange(presetRange("all")); setUnassigned(false); };
  const { data: kanban, isLoading: kLoading } = useQuery({
    queryKey: ["kanban"], queryFn: async () => (await api.get("/leads/kanban")).data, enabled: view === "kanban",
  });

  const openLead = (id) => setParams((p) => { p.set("id", id); return p; });
  const closeLead = () => setParams((p) => { p.delete("id"); return p; });

  return (
    <div>
      <PageHeader title="Leads" subtitle={user?.role === "marketing" ? "Leads you created — see who is handling each one and its current status" : user?.role === "reception" ? "Create enquiries and find existing records" : "Your pipeline — capture once, own it, act next"}
        actions={<><ImportExport /><CreateLeadDialog config={config} /></>} testid="leads-header" />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search name, phone, locality, notes (Hinglish OK)…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-search" />
        </div>
        <Select value={stage || "all"} onValueChange={(v) => setStage(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-52" data-testid="stage-filter"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {(config?.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {user?.role === "admin" && (
          <Button variant={unassigned ? "default" : "outline"} className={unassigned ? "bg-orange-600 hover:bg-orange-700" : ""} onClick={() => setUnassigned((u) => !u)} data-testid="unassigned-toggle">Unassigned</Button>
        )}
        <div className="flex rounded-lg border overflow-hidden">
          <button className={`px-3 ${view === "list" ? "bg-brand-blue text-white" : "bg-white dark:bg-slate-800"}`} onClick={() => setView("list")} data-testid="view-list"><List className="w-4 h-4" /></button>
          <button className={`px-3 ${view === "kanban" ? "bg-brand-blue text-white" : "bg-white dark:bg-slate-800"}`} onClick={() => setView("kanban")} data-testid="view-kanban"><LayoutGrid className="w-4 h-4" /></button>
        </div>
      </div>

      {view === "list" && (
        <FilterShell count={list?.total} onClear={clearFilters} canExport={user?.role === "admin"}
          exportPath="/csv/export/leads" exportParams={leadParams}>
          <DateRange preset={preset} setPreset={setPreset} range={range} setRange={setRange} />
          <FilterSelect value={source} onChange={setSource} placeholder="Source" options={settings?.lead_sources || []} testid="f-source" />
          <FilterSelect value={priority} onChange={setPriority} placeholder="Priority" options={["high", "medium", "low"]} testid="f-priority" width="w-[120px]" />
          {["admin", "reception"].includes(user?.role) && (
            <FilterSelect value={assignedTo} onChange={setAssignedTo} placeholder="Assigned to"
              options={(staff || []).filter((s) => ["sales", "reception"].includes(s.role) && s.active !== false).map((s) => ({ value: s.id, label: s.name }))} testid="f-assigned" width="w-[160px]" />
          )}
          {["admin", "reception"].includes(user?.role) && (
            <FilterSelect value={assignedVia} onChange={setAssignedVia} placeholder="Assigned how"
              options={[{ value: "auto", label: "Auto (rotation)" }, { value: "manual", label: "Manually assigned" }]} testid="f-assigned-via" width="w-[160px]" />
          )}
          <FilterSelect value={lastOutcome} onChange={setLastOutcome} placeholder="Last call result"
            options={[{ value: "no_answer", label: "Not picked" }, { value: "switched_off", label: "Switched off" }, { value: "wrong_number", label: "Wrong number" }, { value: "connected", label: "Connected" }, { value: "busy", label: "Busy" }, { value: "callback_requested", label: "Callback requested" }, { value: "book_demo", label: "Book demo" }, { value: "book_counselling", label: "Book counselling" }]} testid="f-last-outcome" width="w-[170px]" />
          {user?.role !== "marketing" && (
            <FilterSelect value={createdBy} onChange={setCreatedBy} placeholder="Created by"
              options={[{ value: "me", label: "Created by me" }, ...(creators || []).map((c) => ({ value: c.id, label: `${c.name} (${c.role})` }))]} testid="f-created-by" width="w-[180px]" />
          )}
          <FilterSelect value={stale} onChange={setStale} placeholder="Ageing"
            options={[{ value: "7", label: "No contact 7+ days" }, { value: "14", label: "No contact 14+ days" }, { value: "30", label: "No contact 30+ days" }]} testid="f-stale" width="w-[180px]" />
        </FilterShell>
      )}

      {canSelect && view === "list" && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 px-4 py-2" data-testid="bulk-bar">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200" data-testid="bulk-count">{selected.size} lead(s) selected</span>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</Button>
            <Button size="sm" className="bg-brand-blue hover:bg-blue-700 gap-1.5" onClick={() => setAssignOpen(true)} data-testid="bulk-assign-btn"><UserPlus className="w-4 h-4" /> Assign selected</Button>
            {user?.role === "admin" && (
              <Button size="sm" className="bg-red-600 hover:bg-red-700 gap-1.5" onClick={() => setDelOpen(true)} data-testid="bulk-delete-btn"><Trash2 className="w-4 h-4" /> Delete selected</Button>
            )}
          </div>
        </div>
      )}

      {view === "list" ? (
        isLoading ? <Loading /> : !list?.items?.length ? <Empty title="No leads found" subtitle="Add a lead or adjust filters." /> : (
          <div className="rounded-xl border bg-white dark:bg-slate-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 text-xs uppercase">
                  <tr>
                    {canSelect && (
                      <th className="px-3 py-3 w-10">
                        <Checkbox data-testid="lead-select-all" checked={selected.size > 0 && selected.size >= (list?.items?.length || 0)} onCheckedChange={() => toggleAll(list?.items)} />
                      </th>
                    )}
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Phone</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">Locality</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Course</th>
                    <th className="text-left px-4 py-3">Stage</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Next follow-up</th>
                    {(canSelect || user?.role === "marketing") && <th className="text-left px-4 py-3">Assigned to</th>}
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((l) => (
                    <tr key={l.id} onClick={() => openLead(l.id)} className={`border-t hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer ${selected.has(l.id) ? "bg-blue-50/70 dark:bg-blue-950/20" : ""}`} data-testid={`lead-row-${l.id}`}>
                      {canSelect && (
                        <td className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                          <Checkbox data-testid={`lead-select-${l.id}`} checked={selected.has(l.id)} onCheckedChange={() => toggleSelect(l.id)} />
                        </td>
                      )}
                      <td className="px-4 py-3 font-medium">{l.name}{l.demo && <span className="ml-2 text-[10px] text-slate-400">demo</span>}</td>
                      <td className="px-4 py-3 text-slate-600">{l.phone}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-500">{l.locality || "—"}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{l.course_interest || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${STAGE_STYLES[l.stage] || ""}`}>{l.stage}</span></td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{l.next_follow_up_at ? fmtDate(l.next_follow_up_at) : "—"}</td>
                      {(canSelect || user?.role === "marketing") && (
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`lead-assignee-${l.id}`}>
                          {(staff || []).find((s) => s.id === l.assigned_to)?.name || l.assigned_name || <span className="text-orange-600">Unassigned</span>}
                          {l.assigned_via === "auto" && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700" data-testid={`lead-auto-${l.id}`}>Auto</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} total={list?.total} setPage={setPage} />
          </div>
        )
      ) : (
        kLoading ? <Loading /> : (
          <div className="flex gap-4 overflow-x-auto stage-scroll pb-4">
            {(config?.stages || []).map((st) => (
              <div key={st} className="min-w-[260px] w-[260px] shrink-0" data-testid={`kanban-col-${st}`}>
                <div className={`text-xs font-semibold px-2 py-1.5 rounded-md border-l-4 mb-2 ${STAGE_STYLES[st] || ""}`}>
                  {st} <span className="opacity-60">({kanban?.[st]?.count || 0})</span>
                </div>
                <div className="space-y-2">
                  {(kanban?.[st]?.items || []).map((l) => <LeadCard key={l.id} lead={l} onOpen={openLead} />)}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {openId && <LeadDrawer leadId={openId} config={config} onClose={closeLead} onChanged={() => qc.invalidateQueries()} />}

      <BulkAssignDialog open={assignOpen} setOpen={setAssignOpen} ids={[...selected]} staff={staff} role={user?.role}
        onDone={() => { setSelected(new Set()); qc.invalidateQueries(); }} />

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Delete {selected.size} lead(s)?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-300">This permanently deletes the selected leads along with their activities, follow-ups and appointments. Leads that already have an admission are skipped automatically. This cannot be undone.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} data-testid="bulk-delete-cancel">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={bulkDelete.isPending} onClick={() => bulkDelete.mutate()} data-testid="bulk-delete-confirm">
              {bulkDelete.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Delete ${selected.size} lead(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
